import React, { useState, useRef } from "react";
import axios from "axios";
import { Sparkles, Upload, Loader2, Image as ImageIcon, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIHairAnalysis() {
  const [description, setDescription] = useState("");
  const [hairType, setHairType] = useState("");
  const [goals, setGoals] = useState("");
  const [imageB64, setImageB64] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      setError("Please upload a JPG, PNG or WEBP image.");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setError("Image too large (max 4MB).");
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setImagePreview(dataUrl);
      setImageB64(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const analyze = async () => {
    if (!description && !imageB64) {
      setError("Please describe your hair or upload a photo.");
      return;
    }
    setError("");
    setResult("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/hair-analysis`, {
        description,
        hair_type: hairType,
        goals,
        image_base64: imageB64 || null,
      });
      setResult(data.analysis);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderAnalysis = (txt) => {
    const esc = (s) =>
      s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    const bold = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    return txt.split(/\n+/).map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      if (/^#{1,3}\s/.test(trimmed) || /^\*\*.+\*\*:?$/.test(trimmed)) {
        return (
          <h4 key={i} className="font-serif-y text-lg text-[#1a1a1a] mt-4 mb-2">
            {trimmed.replace(/^#{1,3}\s|\*\*/g, "")}
          </h4>
        );
      }
      if (/^[-*]\s/.test(trimmed)) {
        const clean = trimmed.replace(/^[-*]\s/, "");
        return (
          <li key={i} className="font-sans-y text-[14px] text-[#333] ml-5 list-disc"
              dangerouslySetInnerHTML={{ __html: bold(clean) }} />
        );
      }
      return (
        <p key={i} className="font-sans-y text-[14px] leading-relaxed text-[#333] mb-2"
           dangerouslySetInnerHTML={{ __html: bold(trimmed) }} />
      );
    });
  };

  return (
    <section className="section bg-[#FBF8F4]" id="ai-hair-analysis">
      <div className="container-y">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <p className="font-sans-y text-[11px] tracking-[0.35em] uppercase text-[#C9A961] mb-4 inline-flex items-center gap-2">
            <Sparkles size={14} /> Powered by Claude AI
          </p>
          <h2 className="font-serif-y text-4xl md:text-6xl leading-[1.05] text-[#1a1a1a] mb-5">
            AI Hair <span className="italic text-[#C9A961]">Analysis</span>
          </h2>
          <p className="font-sans-y text-base text-[#555] leading-relaxed">
            Get a personalized Ybera routine in seconds. Upload a photo or describe your hair &mdash;
            our AI stylist will recommend the perfect products for you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div className="bg-white p-6 md:p-8 border border-[#EAE3D8]">
            <div className="space-y-5">
              <div>
                <label className="font-sans-y text-[11px] tracking-[0.2em] uppercase text-[#7a7a7a] block mb-2">
                  Upload a photo (optional)
                </label>
                {imagePreview ? (
                  <div className="relative w-full aspect-video bg-[#F5EFE6] overflow-hidden">
                    <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
                    <button
                      onClick={() => { setImagePreview(""); setImageB64(""); }}
                      className="absolute top-2 right-2 w-8 h-8 bg-black/70 text-white flex items-center justify-center hover:bg-black"
                      aria-label="Remove image"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="w-full aspect-video border-2 border-dashed border-[#EAE3D8] hover:border-[#C9A961] flex flex-col items-center justify-center gap-2 text-[#7a7a7a] hover:text-[#C9A961] transition-colors"
                  >
                    <ImageIcon size={28} />
                    <span className="font-sans-y text-[13px]">Click to upload (JPG / PNG / WEBP, max 4MB)</span>
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} />
              </div>

              <div>
                <label className="font-sans-y text-[11px] tracking-[0.2em] uppercase text-[#7a7a7a] block mb-2">
                  Describe your hair
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  placeholder="e.g., Long, frizzy, chemically treated, dry ends..."
                  className="w-full bg-[#FBF8F4] border border-[#EAE3D8] p-3 font-sans-y text-[14px] focus:outline-none focus:border-[#C9A961]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-sans-y text-[11px] tracking-[0.2em] uppercase text-[#7a7a7a] block mb-2">
                    Hair type
                  </label>
                  <input
                    value={hairType}
                    onChange={(e) => setHairType(e.target.value)}
                    placeholder="Curly, Straight, Wavy..."
                    className="w-full bg-[#FBF8F4] border border-[#EAE3D8] p-3 font-sans-y text-[14px] focus:outline-none focus:border-[#C9A961]"
                  />
                </div>
                <div>
                  <label className="font-sans-y text-[11px] tracking-[0.2em] uppercase text-[#7a7a7a] block mb-2">
                    Main goal
                  </label>
                  <input
                    value={goals}
                    onChange={(e) => setGoals(e.target.value)}
                    placeholder="Anti-frizz, Growth, Shine..."
                    className="w-full bg-[#FBF8F4] border border-[#EAE3D8] p-3 font-sans-y text-[14px] focus:outline-none focus:border-[#C9A961]"
                  />
                </div>
              </div>

              {error && (
                <p className="font-sans-y text-[13px] text-red-700 bg-red-50 border border-red-200 p-3">{error}</p>
              )}

              <button
                onClick={analyze}
                disabled={loading}
                className="btn-y-primary w-full justify-center disabled:opacity-60"
              >
                {loading ? (
                  <><Loader2 size={16} className="animate-spin" /> Analyzing your hair...</>
                ) : (
                  <><Sparkles size={16} /> Get my Ybera routine</>
                )}
              </button>
            </div>
          </div>

          <div className="bg-[#F5EFE6] p-6 md:p-8 border border-[#EAE3D8] min-h-[420px]">
            {!result && !loading && (
              <div className="flex flex-col items-center justify-center text-center h-full py-16">
                <div className="w-16 h-16 bg-[#C9A961]/20 flex items-center justify-center mb-5 text-[#C9A961]">
                  <Sparkles size={26} />
                </div>
                <h3 className="font-serif-y text-2xl text-[#1a1a1a] mb-2">Your personalized routine</h3>
                <p className="font-sans-y text-[14px] text-[#666] max-w-xs">
                  Fill in the form and let our AI stylist craft a Ybera routine tailored to you.
                </p>
              </div>
            )}
            {loading && (
              <div className="flex flex-col items-center justify-center text-center h-full py-16">
                <Loader2 size={30} className="animate-spin text-[#C9A961] mb-4" />
                <p className="font-sans-y text-[14px] text-[#666]">Analyzing your hair with Claude AI...</p>
              </div>
            )}
            {result && (
              <div>
                <p className="font-sans-y text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-3">
                  Ybera AI recommendation
                </p>
                <div>{renderAnalysis(result)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
